/**
 * Admin page for settlements (specification §10). Each campaign's `settle` call is prepared once
 * its review has ended, and an admin signed in with the escrow's settler wallet sends it from here
 * before Day 10. An open settlement freezes the campaign's result. Withdrawing it lets a revision
 * be published. If the settler is ever a Safe, the worker proposes the call to the Safe instead,
 * and a withdrawn proposal must then be rejected there.
 */

import { CircleCheck } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useConnection } from "wagmi";

import {
  type Settlement,
  useSendSettlement,
  useSettlements,
  useWithdrawFee,
  useWithdrawSettlement,
} from "../../admin/settlements.js";
import { useSession } from "../../auth/session.js";
import { AddressLink, TransactionLink } from "../../components/ChainLinks.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { Checkbox } from "../../components/forms.js";
import { RequireAdmin } from "../../components/RequireAdmin.js";
import { SignInDialog } from "../../components/SignInDialog.js";
import {
  Badge,
  EmptyState,
  ErrorMessage,
  Fact,
  Loading,
  Notice,
  PageHeader,
  styles,
} from "../../components/ui.js";
import { formatAmount, formatCount, formatUtc } from "../../format.js";
import { describeEscrowError } from "../../wallet/escrow.js";

export function AdminSettlements() {
  return (
    <RequireAdmin>
      <SettlementsPage />
    </RequireAdmin>
  );
}

function SettlementsPage() {
  const [all, setAll] = useState(false);
  const settlements = useSettlements(all ? "all" : "open");
  return (
    <section className="max-w-5xl">
      <PageHeader eyebrow="Admin" title="Settlements" documentTitle="Settlements · Admin">
        Each campaign's settlement, prepared from its latest result once its review has ended. Sign
        in with the settler wallet to send it before Day 10. Morcast never holds the wallet's key.
      </PageHeader>
      <label className="mt-6 flex w-fit cursor-pointer items-center gap-2 text-sm">
        <Checkbox checked={all} onChange={(event) => setAll(event.target.checked)} />
        Show executed, failed and withdrawn settlements too
      </label>
      <div className="mt-6">
        {settlements.isPending && <Loading />}
        {settlements.isError && <ErrorMessage error={settlements.error} />}
        {settlements.data?.length === 0 && (
          <EmptyState icon={CircleCheck} title="No settlement is waiting." />
        )}
        {settlements.data && settlements.data.length > 0 && (
          <ul className="space-y-4">
            {settlements.data.map((settlement) => (
              <SettlementCard key={settlement.id} settlement={settlement} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

const STATUS = {
  prepared: { label: "Ready to send", tone: "amber" },
  proposed: { label: "Proposed to the Safe", tone: "blue" },
  executed: { label: "Executed", tone: "green" },
  failed: { label: "Failed", tone: "red" },
  withdrawn: { label: "Withdrawn", tone: "grey" },
} as const;

function SettlementCard({ settlement }: { settlement: Settlement }) {
  const withdraw = useWithdrawSettlement();
  const [note, setNote] = useState("");
  const open = settlement.status === "prepared" || settlement.status === "proposed";
  // The escrow refuses the call from Day 10 on, so an open settlement past it can never run.
  const closed = open && Date.now() >= Date.parse(settlement.campaign.settlementClosesAt);
  const { label, tone } = closed
    ? ({ label: "Window closed", tone: "red" } as const)
    : STATUS[settlement.status];

  return (
    <li className={styles.card}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold">
          <Link
            to={`/campaigns/${settlement.escrow}/${settlement.campaignId}`}
            className="hover:underline"
          >
            {settlement.campaign.title}
          </Link>
        </h2>
        <Badge tone={tone}>{label}</Badge>
      </div>
      {settlement.campaign.requestId !== null && (
        <Link
          to={`/admin/campaign-requests/${settlement.campaign.requestId}`}
          className={`mt-1 inline-block text-sm ${styles.link}`}
        >
          Request {settlement.campaign.requestId}, with its terms and brief
        </Link>
      )}
      <dl className="mt-3 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
        <Fact label="Budget">
          {formatAmount(settlement.campaign.budget, settlement.campaign.token)}
        </Fact>
        <Fact label="Target">{formatCount(settlement.campaign.target)}</Fact>
        <Fact label="Window">
          {formatUtc(settlement.campaign.startAt)} to {formatUtc(settlement.campaign.endAt)}
        </Fact>
        <Fact label="Creators are paid">
          {formatAmount(settlement.split.pool, settlement.campaign.token)}
        </Fact>
        <Fact label="Morcast's fee">
          {formatAmount(settlement.split.fee, settlement.campaign.token)}
        </Fact>
        <Fact label="Back to the brand">
          {formatAmount(settlement.split.refund, settlement.campaign.token)}
        </Fact>
        <Fact label="Result">
          Revision {settlement.revision}, {formatCount(settlement.recognized)} recognized
        </Fact>
        <Fact label="Prepared">{formatUtc(settlement.preparedAt)}</Fact>
        <Fact label="Settlement closes">{formatUtc(settlement.campaign.settlementClosesAt)}</Fact>
        <Fact label="Brand">
          {settlement.campaign.brand.name}
          <span className="block">
            <AddressLink chainId={settlement.chainId} address={settlement.campaign.brand.wallet} />
          </span>
        </Fact>
        <Fact label="Settler">
          <AddressLink chainId={settlement.chainId} address={settlement.settler} />
        </Fact>
        {settlement.safeTxHash && (
          <Fact label="Safe transaction">
            <span className="font-mono text-xs break-all">{settlement.safeTxHash}</span> (nonce{" "}
            {settlement.safeNonce})
          </Fact>
        )}
        {settlement.executedTx && (
          <Fact label="Executed">
            <TransactionLink chainId={settlement.chainId} hash={settlement.executedTx} />
          </Fact>
        )}
      </dl>
      {closed && (
        <Notice tone="warning" className="mt-4">
          The settlement window closed on Day 10, so the escrow no longer takes this call. The brand
          can take its whole budget back, and the creators are not paid.
        </Notice>
      )}
      {settlement.status === "prepared" && !closed && <SendSettlement settlement={settlement} />}
      {settlement.status === "executed" && BigInt(settlement.split.fee) > 0n && (
        <FeeWithdrawal settlement={settlement} />
      )}
      {open && !closed && (
        <div className="mt-4 text-sm">
          <p className="font-medium">The call</p>
          <dl className="mt-1 space-y-2">
            <Fact label="To (the escrow)">
              <code className="font-mono text-xs break-all">{settlement.call.to}</code>
            </Fact>
            <Fact label="Value">{settlement.call.value}</Fact>
            <Fact label="Data">
              <code className="font-mono text-xs break-all">{settlement.call.data}</code>
            </Fact>
          </dl>
          <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400">
            Withdraw it only when this call must not run, for example because a decision is about to
            revise the result. The worker then prepares the call again from the latest result, and
            an unchanged result gives the same call.
          </p>
          <label className="mt-3 block font-medium">
            Why withdraw it
            <textarea
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              className={styles.input}
            />
          </label>
          <button
            type="button"
            disabled={withdraw.isPending || note.trim().length < 5}
            onClick={() => withdraw.mutate({ id: settlement.id, note: note.trim() })}
            className={`mt-3 ${styles.dangerButton}`}
          >
            Withdraw
          </button>
          {settlement.status === "proposed" && (
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              After withdrawing, reject the queued transaction in the Safe.
            </p>
          )}
          {withdraw.isError && <ErrorMessage error={withdraw.error} />}
        </div>
      )}
      {settlement.note && (
        <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-400">
          <span className="font-medium">Withdrawn:</span> {settlement.note}
        </p>
      )}
    </li>
  );
}

/**
 * Sends a prepared settlement from the settler wallet, which the admin must be signed in with. The
 * escrow pays out on it for good, so the page asks first.
 */
function SendSettlement({ settlement }: { settlement: Settlement }) {
  const send = useSendSettlement();
  const { data: session } = useSession();
  const { isConnected } = useConnection();
  const [confirming, setConfirming] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const isSettler = session?.wallet === settlement.settler.toLowerCase();

  if (send.data) {
    return (
      <p className="mt-4 flex flex-wrap items-center gap-2 text-sm">
        <Badge tone="green">Sent</Badge>
        <TransactionLink chainId={settlement.chainId} hash={send.data} />
        <span className="text-zinc-600 dark:text-zinc-400">
          It shows as executed once the worker reads the escrow's event.
        </span>
      </p>
    );
  }
  return (
    <div className="mt-4 text-sm">
      {isSettler ? (
        <>
          <button
            type="button"
            disabled={send.isPending}
            onClick={() => (isConnected ? setConfirming(true) : setSigningIn(true))}
            className={styles.primaryButton}
          >
            {send.isPending ? "Sending…" : "Send settlement"}
          </button>
          {!isConnected && (
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              Your wallet is not connected, so sending signs you in with it again first.
            </p>
          )}
        </>
      ) : (
        <p className="text-zinc-600 dark:text-zinc-400">
          Sign in with the settler wallet to send it.
        </p>
      )}
      <ConfirmDialog
        open={confirming}
        title="Send this settlement?"
        confirmLabel="Send settlement"
        cancelLabel="Not now"
        tone="primary"
        onConfirm={() => send.mutate(settlement)}
        onClose={() => setConfirming(false)}
      >
        <p>
          The escrow settles {settlement.campaign.title} with revision {settlement.revision} of its
          result. Creators can then claim their payouts, and a settlement cannot be undone.
        </p>
      </ConfirmDialog>
      <SignInDialog open={signingIn} onClose={() => setSigningIn(false)} />
      {send.isError && (
        <Notice tone="error" className="mt-3">
          {describeEscrowError(send.error)}
        </Notice>
      )}
    </div>
  );
}

/**
 * A settled campaign's fee: withdrawn to Morcast's treasury, or still in the escrow with a button
 * that withdraws it. The escrow sends it to the treasury, the settler wallet (D28), whoever asks,
 * so any signed-in admin can collect it whenever the team wants.
 */
function FeeWithdrawal({ settlement }: { settlement: Settlement }) {
  const withdraw = useWithdrawFee();
  const { isConnected } = useConnection();
  const [signingIn, setSigningIn] = useState(false);
  const fee = formatAmount(settlement.split.fee, settlement.campaign.token);
  const { feeWithdrawal } = settlement;

  if (feeWithdrawal) {
    return (
      <p className="mt-4 flex flex-wrap items-center gap-2 text-sm">
        <Badge tone="green">Fee withdrawn</Badge>
        <span>
          {fee} went to Morcast's treasury on {formatUtc(feeWithdrawal.at)} in{" "}
          <TransactionLink chainId={settlement.chainId} hash={feeWithdrawal.tx} />.
        </span>
      </p>
    );
  }
  if (withdraw.data) {
    return (
      <p className="mt-4 flex flex-wrap items-center gap-2 text-sm">
        <Badge tone="green">Sent</Badge>
        <TransactionLink chainId={settlement.chainId} hash={withdraw.data} />
        <span className="text-zinc-600 dark:text-zinc-400">
          The fee shows as withdrawn once the worker reads the escrow's event.
        </span>
      </p>
    );
  }
  return (
    <div className="mt-4 text-sm">
      <p className="text-zinc-700 dark:text-zinc-300">
        Morcast's fee of {fee} is waiting in the escrow. Withdrawing it sends it to the treasury,
        the settler wallet, from whichever wallet you use.
      </p>
      <button
        type="button"
        disabled={withdraw.isPending}
        onClick={() => (isConnected ? withdraw.mutate(settlement) : setSigningIn(true))}
        className={`mt-3 ${styles.primaryButton}`}
      >
        {withdraw.isPending ? "Withdrawing…" : "Withdraw fee"}
      </button>
      <SignInDialog open={signingIn} onClose={() => setSigningIn(false)} />
      {withdraw.isError && (
        <Notice tone="error" className="mt-3">
          {describeEscrowError(withdraw.error)}
        </Notice>
      )}
    </div>
  );
}
